import { FormattedMessage, useIntl } from "react-intl";
import {
  formatMegabytes,
  MAX_APPLICATION_DOCUMENTS,
  MAX_DOCUMENT_UPLOAD_BYTES,
} from "@/lib/upload-limits";
import {
  MAX_OTHER_APPLICATION_DOCUMENTS,
  type ApplicationDocumentProblem,
  type ApplicationDocuments,
  type DocumentSlotKind,
} from "./application-documents";
import { ApplicationDocumentSlot } from "./ApplicationDocumentSlot";

// Die Dokumente einer Bewerbung, für den Dialog mit Konto und das öffentliche Formular gleich: ein Platz für den
// Lebenslauf (bei einer Unternehmensbewerbung für das Unternehmensprofil), daneben weitere Dokumente, darunter die Grenzen und
// was nicht passte. Die Gesamtgrenze bringt der Aufrufer mit, denn sie hängt am Transport, nicht an der Bewerbung.
export function ApplicationDocumentsSection({
  idPrefix,
  documents,
  problems,
  company,
  totalLimit,
  disabled,
  onFiles,
  onRemoveCv,
  onRemoveOther,
}: {
  idPrefix: string;
  documents: ApplicationDocuments;
  problems: ApplicationDocumentProblem[];
  company: boolean;
  totalLimit: number;
  disabled: boolean;
  onFiles: (slot: DocumentSlotKind, files: File[]) => void;
  onRemoveCv: () => void;
  onRemoveOther: (index: number) => void;
}) {
  const intl = useIntl();
  return (
    <section className="mt-6">
      <h3 className="text-smycles-body font-medium">
        <FormattedMessage id="share.applications.documents.title" defaultMessage="Dokumente" />
      </h3>
      <div className="mt-4 space-y-4">
        <ApplicationDocumentSlot
          idPrefix={`${idPrefix}-cv`}
          label={intl.formatMessage({ id: company ? "share.applications.company.profile" : "share.applications.documents.cv" })}
          files={documents.cv ? [documents.cv] : []}
          multiple={false}
          canAdd={!documents.cv}
          disabled={disabled}
          onFiles={(files) => onFiles("cv", files)}
          onRemove={onRemoveCv}
        />
        <ApplicationDocumentSlot
          idPrefix={`${idPrefix}-more`}
          label={intl.formatMessage({ id: "share.applications.documents.more", defaultMessage: "Weitere Dokumente" })}
          files={documents.others}
          multiple
          canAdd={documents.others.length < MAX_OTHER_APPLICATION_DOCUMENTS}
          disabled={disabled}
          onFiles={(files) => onFiles("others", files)}
          onRemove={onRemoveOther}
        />
      </div>
      <p className="smycles-field-meta mt-2 text-ink-muted">
        <FormattedMessage
          id="share.applications.documents.limits"
          defaultMessage="PDF, Word, Excel, Text oder Bild. Höchstens {count} Dateien, je bis {perFile}, zusammen bis {total}."
          values={{
            count: MAX_APPLICATION_DOCUMENTS,
            perFile: formatMegabytes(MAX_DOCUMENT_UPLOAD_BYTES),
            total: formatMegabytes(totalLimit),
          }}
        />
      </p>
      {problems.length ? (
        <ul id={`${idPrefix}-document-problems`} role="alert" className="mt-2 space-y-1 text-smycles-meta text-ink-accent">
          {problems.map((problem, index) => (
            <li key={`${problem.kind}-${problem.name}-${index}`}>
              <DocumentProblem problem={problem} totalLimit={totalLimit} />
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}

function DocumentProblem({ problem, totalLimit }: { problem: ApplicationDocumentProblem; totalLimit: number }) {
  const name = problem.name;
  switch (problem.kind) {
    case "tooLarge":
      return (
        <FormattedMessage
          id="share.applications.documents.tooLarge"
          defaultMessage="{name} ist größer als {max}."
          values={{ name, max: formatMegabytes(MAX_DOCUMENT_UPLOAD_BYTES) }}
        />
      );
    case "unsupportedType":
      return (
        <FormattedMessage
          id="share.applications.documents.unsupportedType"
          defaultMessage="{name}: Dieses Format wird nicht angenommen."
          values={{ name }}
        />
      );
    case "empty":
      return (
        <FormattedMessage id="share.applications.documents.empty" defaultMessage="{name} ist leer." values={{ name }} />
      );
    case "tooMany":
      return (
        <FormattedMessage
          id="share.applications.documents.tooMany"
          defaultMessage="{name} passt nicht mehr dazu: höchstens ein Lebenslauf und {count} weitere Dokumente."
          values={{ name, count: MAX_OTHER_APPLICATION_DOCUMENTS }}
        />
      );
    case "totalTooLarge":
      return (
        <FormattedMessage
          id="share.applications.documents.totalTooLarge"
          defaultMessage="{name} passt nicht mehr dazu: zusammen höchstens {max}."
          values={{ name, max: formatMegabytes(totalLimit) }}
        />
      );
  }
}
